/* ============================================
   QRLoginDisplay — QR code for passwordless login
   Shows QR → polls status → auto-login on approval
   ============================================ */

import { useState, useEffect, useRef, useCallback } from 'react'
import QRCode from 'qrcode'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'
const POLL_INTERVAL = 3000

interface QRLoginDisplayProps {
  onSwitchToPassword: () => void
  isPortal?: boolean
}

type QRState = 'loading' | 'ready' | 'approved' | 'expired' | 'error'

export default function QRLoginDisplay({ onSwitchToPassword, isPortal }: QRLoginDisplayProps) {
  const [state, setState] = useState<QRState>('loading')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const cleanup = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  const createSession = useCallback(async () => {
    cleanup()
    setState('loading')
    setError(null)

    try {
      const res = await fetch(`${API_URL}/api/auth/qr/session`, { method: 'POST' })
      if (!res.ok) throw new Error('Error al crear sesión')
      const data = await res.json()

      const expiry = new Date(data.expires_at).getTime()
      setTimeLeft(Math.max(0, Math.floor((expiry - Date.now()) / 1000)))

      // Generate QR code
      const approveUrl = `${window.location.origin}/qr-login/approve/${data.session_id}`
      const dataUrl = await QRCode.toDataURL(approveUrl, {
        width: 280,
        margin: 2,
        color: { dark: 'var(--primary)', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      })
      setQrDataUrl(dataUrl)
      setState('ready')

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/auth/qr/session/${data.session_id}/status`)
          if (!statusRes.ok) return
          const statusData = await statusRes.json()

          if (statusData.status === 'approved' && statusData.token) {
            cleanup()
            setState('approved')
            localStorage.setItem('auth_token', statusData.token)
            setTimeout(() => {
              window.location.href = isPortal ? '/portal' : '/'
            }, 1500)
          } else if (statusData.status === 'expired') {
            cleanup()
            setState('expired')
          }
        } catch {
          // Ignore polling errors
        }
      }, POLL_INTERVAL)

      // Start countdown
      timerRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000))
        setTimeLeft(remaining)
        if (remaining <= 0) {
          cleanup()
          setState('expired')
        }
      }, 1000)

    } catch (err) {
      setState('error')
      setError(err instanceof Error ? err.message : 'Error de conexión')
    }
  }, [cleanup, isPortal])

  useEffect(() => {
    createSession()
    return cleanup
  }, [createSession, cleanup])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const timerClass = timeLeft <= 15 ? 'qr-login-timer--critical' : timeLeft <= 30 ? 'qr-login-timer--warning' : ''

  if (state === 'approved') {
    return (
      <div className="qr-login-approved" id="qr-login-approved">
        <div className="qr-login-approved-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3>¡Login aprobado!</h3>
        <p>Redireccionando...</p>
      </div>
    )
  }

  if (state === 'expired') {
    return (
      <div className="qr-login-expired" id="qr-login-expired">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        <p>El código QR ha expirado</p>
        <button type="button" className="qr-login-refresh-btn" onClick={createSession} id="qr-refresh-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Generar nuevo código
        </button>
        <button type="button" className="qr-login-switch" onClick={onSwitchToPassword}>
          Usar contraseña en su lugar →
        </button>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="qr-login-expired" id="qr-login-error">
        <p>{error || 'Error al generar código QR'}</p>
        <button type="button" className="qr-login-refresh-btn" onClick={createSession} id="qr-retry-btn">
          Reintentar
        </button>
        <button type="button" className="qr-login-switch" onClick={onSwitchToPassword}>
          Usar contraseña en su lugar →
        </button>
      </div>
    )
  }

  return (
    <div className="qr-login-display" id="qr-login-display">
      {state === 'loading' ? (
        <div className="qr-login-code-wrapper">
          <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="login-spinner" />
          </div>
        </div>
      ) : (
        <>
          <div className="qr-login-code-wrapper">
            {qrDataUrl && <img src={qrDataUrl} alt="QR Login Code" />}
            <div className="qr-login-brand-overlay">
              <img src="/assets/brand/logo-icon-color.svg" alt="1C" />
            </div>
          </div>
          <p className="qr-login-status">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
            Escanea con tu app 1Conecta
          </p>
          <p className={`qr-login-timer ${timerClass}`}>
            Expira en {formatTime(timeLeft)}
          </p>
        </>
      )}
      <button type="button" className="qr-login-switch" onClick={onSwitchToPassword}>
        Usar contraseña en su lugar →
      </button>
    </div>
  )
}
